import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Users2, Building2, PhoneCall } from "lucide-react";
import Contacts from "@/pages/Contacts";
import Departments from "@/pages/Departments";
import CompanyRoster from "@/pages/CompanyRoster";

const TABS = [
  { id: "directory", label: "Directory", icon: PhoneCall, hint: "Cast, crew, and staff — searchable phone & email list." },
  { id: "roster", label: "Company roster", icon: Users2, hint: "Add & manage everyone who belongs to this production." },
  { id: "departments", label: "Departments", icon: Building2, hint: "Group members into Music, Stage Management, Design, and more." },
];

export default function People() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") || "directory";
  const [tab, setTab] = useState(TABS.find((t) => t.id === initial) ? initial : "directory");

  const visibleTabs = useMemo(() => {
    // Cast users don't need the full roster/departments management surface, but they can still view.
    return TABS;
  }, [user.role]);

  const setActive = (id) => {
    setTab(id);
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  const active = TABS.find((t) => t.id === tab) || TABS[0];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="people-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Company</div>
        <h1 className="mt-1 font-display text-4xl tracking-tight font-light">People</h1>
        <p className="mt-1 text-zinc-400 text-sm">{active.hint}</p>
      </header>

      <div className="flex flex-wrap gap-1 border border-white/10 rounded-md bg-[#121212] p-1 w-fit" data-testid="people-tabs">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`people-tab-${id}`}
            onClick={() => setActive(id)}
            className={`h-9 px-3 rounded text-sm inline-flex items-center gap-1.5 transition-colors
              ${tab === id ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="pt-2">
        {tab === "directory" && <Contacts embedded />}
        {tab === "roster" && <CompanyRoster />}
        {tab === "departments" && <Departments embedded />}
      </div>
    </div>
  );
}
